import {useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from "react";
import {useLocation, useNavigate, useParams, useSearchParams} from "react-router-dom";
import {useDialogContext} from "../context/DialogContextProvider";
import {useDispatch} from "react-redux";
import {lang} from "../lang";
import {account_actions} from "../redux/slice/AccountReducer";
import {cache_actions} from "../redux/slice/CacheReducer";
import {storage_remove} from "../common";

export const useMountEffect = (on_mount_effect) => {
    const is_mount = useRef(false);
    const mount_count = useRef(0);

    const isMount = useCallback(() => {
        return is_mount.current;
    }, []);

    useLayoutEffect(() => {
        is_mount.current = true;
        mount_count.current = mount_count.current + 1;

        if (mount_count.current === 1) {
            // 최초 한번만 실행
            on_mount_effect && on_mount_effect();
        }

        return () => {
            is_mount.current = false;
        };
    }, [on_mount_effect]);

    return [isMount];
};

export const useResize = (on_resize) => {
    const onResize = useCallback(() => {
        on_resize && on_resize(window.innerWidth, window.innerHeight);
    }, [on_resize]);

    useEffect(() => {
        onResize();
        window.addEventListener("resize", onResize);

        return () => {
            window.removeEventListener("resize", onResize);
        };
    }, [onResize]);
};

export const useScrollEvent = (on_scroll) => {
    const onScroll = useCallback(
        (event) => {
            on_scroll &&
                on_scroll({
                    x: window.scrollX,
                    y: window.scrollY,
                    event,
                });
        },
        [on_scroll]
    );

    useEffect(() => {
        onScroll();
        window.addEventListener("scroll", onScroll);

        return () => {
            window.removeEventListener("scroll", onScroll);
        };
    }, [onScroll]);
};

export const MOBILE_WIDTH = 768;
export const useMobileDetect = () => {
    const isMobile = useCallback((width) => {
        return width < MOBILE_WIDTH;
    }, []);

    const [is_mobile, setMobile] = useState(isMobile(window.innerWidth));

    useResize(
        useCallback(
            (width) => {
                setMobile(isMobile(width));
            },
            [isMobile]
        )
    );

    return {is_mobile};
};

// get pathname -> params(object)
// get url parameter -> searchParams.get(string)
// get state -> state(object)
export const useParameters = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const {state} = useLocation();
    const params = useParams();
    return useMemo(() => {
        return {
            searchParams,
            setSearchParams,
            params,
            state,
        };
    }, [searchParams, setSearchParams, params, state]);
};

export const useBeforeUnload = (on_before_unload) => {
    const onBeforeUnload = useCallback(
        (event) => {
            let is_blocking = on_before_unload && on_before_unload(event);

            if (is_blocking) {
                event.preventDefault();
                event.returnValue = "";
            }
        },
        [on_before_unload]
    );

    useEffect(() => {
        window.addEventListener("beforeunload", onBeforeUnload);
        return () => {
            window.removeEventListener("beforeunload", onBeforeUnload);
        };
    }, [onBeforeUnload]);
};

export const useSetDocumentTitle = (title) => {
    useEffect(() => {
        window.document.querySelector("title").innerHTML = title || "베어유 비즈니스에서 커리어 성장을 이루세요";
    }, [title]);
};

export const useLogout = () => {
    const {confirm} = useDialogContext();
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const doLogout = async () => {
        if (await confirm(lang.login.confirm_logout)) {
            dispatch(account_actions.doLogout());
            dispatch(cache_actions.removeAll());
            storage_remove("auth_info");

            navigate("/");
        } else {
            return false;
        }
    };

    return {doLogout};
};
